import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ApiError} from "../api/client";
import { apiGet } from "../api/client";

interface Spot {
  id: string;
  workspaceId: string;
  kind: string;
  title: string;
  description: string;
  state: "assessing" | "awaiting_approval" | "actioned" | "dismissed" | "expired";
  currentDisposition: string | null;
  createdAt: string;
  outcomeAt: string | null;
}

interface SpotAction {
  id: string;
  spotId: string;
  coa: string;
  rationale: string;
  approverNote: string | null;
  approvedAt: string | null;
  outcome: string | null;
  outcomeAt: string | null;
}

const KIND_LABELS: Record<string, string> = {
  risk: "Risk",
  opportunity: "Opportunity",
  client_request: "Client request",
  operational: "Operational",
  other: "Other",
};

const STATE_LABELS: Record<string, string> = {
  assessing: "Assessing",
  awaiting_approval: "Awaiting approval",
  actioned: "Actioned",
  dismissed: "Dismissed",
  expired: "Expired",
};

export function MySpotted() {
  const navigate = useNavigate();
  const spotsQ = useQuery<{ spots: Spot[] }, ApiError>({
    queryKey: ["me", "spots"],
    queryFn: () => apiGet("/api/me/spots"),
  });

  // For each actioned spot, pull the latest action so we can show its outcome.
  // Phase A keeps it simple — one detail fetch per actioned spot. Good
  // enough for the personal view (rarely >50 items).
  const actionable = useMemo(
    () => (spotsQ.data?.spots ?? []).filter((s) => s.state === "actioned"),
    [spotsQ.data],
  );

  return (
    <main className="review-page" style={{ margin: "0 auto" }}>
      <p className="review-eyebrow">my spotted</p>
      <h1 className="review-title">What came of what you reported</h1>

      {spotsQ.isLoading ? (
        <div style={{ marginTop: 24, color: "var(--text-3, #64748b)" }}>Loading…</div>
      ) : (spotsQ.data?.spots ?? []).length === 0 ? (
        <div className="ho-empty" style={{ marginTop: 24 }}>
          You haven't reported any spots yet. Use the <strong>Spotted</strong> menu in any
          workspace to flag something — XAMI assesses it and you'll see the outcome here.
        </div>
      ) : (
        <ul className="review-comment-list" style={{ marginTop: 20 }}>
          {(spotsQ.data?.spots ?? []).map((s) => (
            <MySpotRow
              key={s.id}
              spot={s}
              showOutcome={s.state === "actioned"}
              actionable={actionable.includes(s)}
              onOpen={() =>
                navigate(`/workspaces/${s.workspaceId}/spotted/${s.id}`)
              }
            />
          ))}
        </ul>
      )}
    </main>
  );
}

function MySpotRow({
  spot,
  showOutcome,
  onOpen,
}: {
  spot: Spot;
  showOutcome: boolean;
  actionable: boolean;
  onOpen: () => void;
}) {
  const detailQ = useQuery<{ spot: Spot; actions: SpotAction[] }, ApiError>({
    queryKey: ["spot", spot.workspaceId, spot.id],
    queryFn: () =>
      apiGet(`/api/workspaces/${spot.workspaceId}/spots/${spot.id}`),
    enabled: showOutcome,
    retry: false,
  });
  const latestAction = detailQ.data?.actions?.[detailQ.data.actions.length - 1];

  return (
    <li
      className="review-comment"
      style={{ cursor: "pointer" }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
    >
      <div className="review-comment-head">
        <span className="author">{spot.title}</span>
        <span className={`review-comment-decision ${stateTone(spot.state)}`}>
          {STATE_LABELS[spot.state] ?? spot.state}
        </span>
        {spot.currentDisposition && (
          <span className={`coa-pill coa-${spot.currentDisposition}`}>
            {dispositionLabel(spot.currentDisposition)}
          </span>
        )}
        <span>· {KIND_LABELS[spot.kind] ?? spot.kind}</span>
        <span style={{ marginLeft: "auto", fontSize: 11 }}>
          {new Date(spot.createdAt).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
          })}
        </span>
      </div>
      {latestAction?.outcome && (
        <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-2, #475569)" }}>
          <strong>Outcome:</strong> {latestAction.outcome}
        </div>
      )}
      {latestAction && !latestAction.outcome && (
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-3, #64748b)" }}>
          {latestAction.coa === "signal"
            ? "Filed as signal — XI will revisit at the next analysis cycle."
            : "In progress — outcome will appear here when the triggered work completes."}
        </div>
      )}
    </li>
  );
}

function stateTone(state: string): "approve" | "return" | "note" {
  if (state === "actioned") return "approve";
  if (state === "dismissed" || state === "expired") return "return";
  return "note";
}

function dispositionLabel(disposition: string): string {
  switch (disposition) {
    case "attach": return "Attached";
    case "new_play": return "New play";
    case "new_card": return "Decision card";
    case "signal": return "Signal";
    case "dismissed": return "Dismissed";
    default: return disposition;
  }
}
